/**
 * Avatar + account menu for the learn top bar. Shown once a learner session
 * exists; it is the only place a learner can reach their profile or sign out.
 */

import { useEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { LogOut, User as UserIcon, BarChart3 } from 'lucide-react';
import { useLearnAuth } from './LearnAuthProvider';

const ITEM =
  'flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm text-[#56505F] transition-colors hover:bg-black/[0.04] hover:text-[#1B1826] dark:text-[#B2B0C0] dark:hover:bg-white/[0.06] dark:hover:text-white';

function initials(name?: string | null, email?: string): string {
  const source = name?.trim() || email?.split('@')[0] || '?';
  const parts = source.split(/[\s._-]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || source[0].toUpperCase();
}

export function LearnAccountMenu() {
  const { profile, signOut } = useLearnAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!profile) return null;

  const label = profile.displayName || profile.email;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account: ${label}`}
        className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full border border-black/10 bg-[#7C3AED] text-[13px] font-semibold text-white transition-opacity hover:opacity-90 dark:border-white/15"
      >
        {profile.photoUrl ? (
          <img src={profile.photoUrl} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer" />
        ) : (
          initials(profile.displayName, profile.email)
        )}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-2 w-60 overflow-hidden rounded-2xl border border-black/10 bg-white shadow-xl dark:border-white/12 dark:bg-[#16141D]"
        >
          <div className="border-b border-black/[0.07] px-4 py-3 dark:border-white/10">
            <p className="truncate text-sm font-semibold text-[#1B1826] dark:text-[#F2F1F6]">
              {profile.displayName || 'Learner'}
            </p>
            <p className="truncate text-xs text-[#8A8593]">{profile.email}</p>
          </div>
          <Link to="/learn/me" role="menuitem" onClick={() => setOpen(false)} className={ITEM}>
            <BarChart3 className="h-4 w-4" />
            My learning
          </Link>
          <Link
            to="/learn/profile"
            role="menuitem"
            onClick={() => setOpen(false)}
            className={ITEM}
          >
            <UserIcon className="h-4 w-4" />
            Profile
          </Link>
          <button
            role="menuitem"
            onClick={() => void signOut()}
            className={ITEM}
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}
